import type { MetricSeriesConfig } from '@hooks/graph/useMetricChart';

/**
 * A chart with twenty series is a tangle. Keep the largest few, fold the
 * rest into one "Other" series summed per row, and say how many were
 * folded so the caller can offer to show them all.
 */
export const DEFAULT_MAX_SERIES = 6;
export const OTHER_SERIES_ID = '__other';

export interface CappedSeries<T extends object> {
    data: T[];
    series: MetricSeriesConfig<T>[];
    /** Labels folded into "Other"; empty when nothing was folded. */
    folded: string[];
}

export function capSeries<T extends object>(
    data: T[],
    series: MetricSeriesConfig<T>[],
    max = DEFAULT_MAX_SERIES,
): CappedSeries<T> {
    // Folding one series into "Other" saves nothing; only fold when it hides at least two.
    if (series.length <= max + 1) return { data, series, folded: [] };

    const total = (s: MetricSeriesConfig<T>): number =>
        data.reduce((sum, row) => sum + Math.abs(Number(row[s.key] ?? 0) || 0), 0);
    const ranked = [...series].sort((a, b) => total(b) - total(a));
    const kept = ranked.slice(0, max);
    const rest = ranked.slice(max);
    const keptIds = new Set(kept.map((s) => s.id));

    const folded = data.map((row) => {
        const other = rest.reduce((sum, s) => sum + (Number(row[s.key] ?? 0) || 0), 0);
        return { ...row, [OTHER_SERIES_ID]: other } as T;
    });
    const otherSeries: MetricSeriesConfig<T> = {
        id: OTHER_SERIES_ID,
        key: OTHER_SERIES_ID as keyof T,
        label: `Other (${rest.length})`,
        color: '#9ca3af',
        renderAs: rest[0]?.renderAs,
    };

    return {
        data: folded,
        // Keep the caller's order for the kept series so colours stay stable.
        series: [...series.filter((s) => keptIds.has(s.id)), otherSeries],
        folded: rest.map((s) => s.label),
    };
}
